<script setup lang="ts">
/**
 * FE-024 — mobile bottom nav bar (Figma `Bottomnav bar` 11:39225).
 *
 * A white plate with a circular cut-out at the top centre; the AIRA ball
 * (`AiraFloatingWidget`, docked via `useFairBottomNav`) rises out of it. Four
 * icon tabs, no labels, a dot under the current one. Slides away on scroll
 * down, back on scroll up (agreed with design, not in the file).
 *
 * FE-017 (animated icons): Material «emphasis» — the glyph's own parts move
 * when the tab becomes current (house lands, hanger swings, heart beats and
 * fills, head nods), the dot scales in, a press squeezes the glyph. All CSS,
 * all off under `prefers-reduced-motion`. Motion per tab: `data-motion`.
 */
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { FairIcon } from '@/ui/primitives/FairIcon'
import { useAuthModal, useTokenStore } from '@/features/auth/store'
import {
  FAIR_BOTTOM_NAV_END_TABS,
  FAIR_BOTTOM_NAV_ROOT_CLASS,
  FAIR_BOTTOM_NAV_START_TABS,
  resolveFairBottomNavActive,
  resolveFairBottomNavScrollIntent,
  type FairBottomNavTab,
} from '../fair-bottom-nav.data'
import { useFairBottomNav } from '../store/useFairBottomNavStore'
import './fair-bottom-nav.css'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const tokenStore = useTokenStore()
const authModal = useAuthModal()
const nav = useFairBottomNav()

const activeTab = computed(() => resolveFairBottomNavActive(route.path))
const hidden = computed(() => nav.hidden)

function isActive(tab: FairBottomNavTab): boolean {
  return activeTab.value === tab.id
}

function select(tab: FairBottomNavTab) {
  if (tab.auth && !tokenStore.accessToken) {
    authModal.showAuthModal('signInSignUpWrapper')
    return
  }
  if (isActive(tab) && route.path === tab.to) return
  void router.push(tab.to)
}

/* ——— Hide on scroll down, show on scroll up ——— */
let lastY = 0
function onScroll() {
  if (nav.claimed) return
  const y = window.scrollY
  const intent = resolveFairBottomNavScrollIntent({ scrollY: y, lastY, hidden: nav.hidden })
  if (intent) nav.setHidden(intent === 'hide')
  lastY = Math.max(0, y)
}

onMounted(() => {
  document.documentElement.classList.add(FAIR_BOTTOM_NAV_ROOT_CLASS)
  nav.setMounted(true)
  lastY = window.scrollY
  window.addEventListener('scroll', onScroll, { passive: true })
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll)
  document.documentElement.classList.remove(FAIR_BOTTOM_NAV_ROOT_CLASS)
  nav.setMounted(false)
})

// A new page starts with the bar on screen.
watch(() => route.path, () => {
  nav.setHidden(false)
  lastY = 0
})
</script>

<template>
  <nav
    class="fair-bottom-nav"
    :class="{ 'fair-bottom-nav--hidden': hidden }"
    :aria-label="t('fair_bottom_nav.aria_label')"
    :aria-hidden="hidden ? 'true' : undefined"
    :inert="hidden || undefined"
    data-figma="11:39225"
  >
    <div class="fair-bottom-nav__plate">
      <!-- Figma `Subtract` 11:39228: plate 393×68 r20 minus a smooth notch (S-shoulders, 164px wide, 62px deep).
           Three pieces so the notch keeps its exact curve at any viewport width. -->
      <div
        class="fair-bottom-nav__shape"
        aria-hidden="true"
      >
        <div class="fair-bottom-nav__shape-side" />
        <div class="fair-bottom-nav__shape-notch">
          <svg
            class="fair-bottom-nav__shape-svg"
            viewBox="0 0 164 68"
            width="164"
            height="68"
            preserveAspectRatio="none"
          >
            <path d="M0 0C11.046 0 19.682 9.185 23.182 19.6614C31.403 44.2657 54.631 62 82 62C109.369 62 132.597 44.2657 140.818 19.6614C144.318 9.185 152.954 0 164 0V68H0Z" />
          </svg>
          <div class="fair-bottom-nav__shape-fill" />
        </div>
        <div class="fair-bottom-nav__shape-side" />
      </div>

      <div class="fair-bottom-nav__group">
        <button
          v-for="tab in FAIR_BOTTOM_NAV_START_TABS"
          :key="tab.id"
          type="button"
          class="fair-bottom-nav__tab"
          :class="{ 'fair-bottom-nav__tab--active': isActive(tab) }"
          :aria-label="t(`fair_bottom_nav.${tab.labelKey}`)"
          :aria-current="isActive(tab) ? 'page' : undefined"
          :data-tab="tab.id"
          :data-motion="tab.motion"
          @click="select(tab)"
        >
          <span class="fair-bottom-nav__glyph">
            <FairIcon
              :name="tab.icon"
              :size="20"
              :parts="tab.parts"
              class="fair-bottom-nav__icon"
            />
            <FairIcon
              v-if="tab.activeIcon"
              :name="tab.activeIcon"
              :size="20"
              class="fair-bottom-nav__icon fair-bottom-nav__icon--active"
            />
          </span>
          <span class="fair-bottom-nav__dot" />
        </button>
      </div>

      <!-- The AIRA ball (AiraFloatingWidget, docked) floats above this gap. -->
      <div
        class="fair-bottom-nav__center"
        aria-hidden="true"
      />

      <div class="fair-bottom-nav__group">
        <button
          v-for="tab in FAIR_BOTTOM_NAV_END_TABS"
          :key="tab.id"
          type="button"
          class="fair-bottom-nav__tab"
          :class="{ 'fair-bottom-nav__tab--active': isActive(tab) }"
          :aria-label="t(`fair_bottom_nav.${tab.labelKey}`)"
          :aria-current="isActive(tab) ? 'page' : undefined"
          :data-tab="tab.id"
          :data-motion="tab.motion"
          @click="select(tab)"
        >
          <span class="fair-bottom-nav__glyph">
            <FairIcon
              :name="tab.icon"
              :size="20"
              :parts="tab.parts"
              class="fair-bottom-nav__icon"
            />
            <FairIcon
              v-if="tab.activeIcon"
              :name="tab.activeIcon"
              :size="20"
              class="fair-bottom-nav__icon fair-bottom-nav__icon--active"
            />
          </span>
          <span class="fair-bottom-nav__dot" />
        </button>
      </div>
    </div>
  </nav>
</template>
